import type { HeroVariant } from '../shell';

// The header's collapse button overrides the person's default for that page only (DESIGN-SPEC
// §2); the default itself is changed only in My settings (K4). The override is a per-viewer
// convenience, kept in this browser: it may be missing (private window, cleared site data) and
// then the page simply opens with the default.

const STORAGE_KEY = 'mrv2.heroOverrides';

/** Page → header form, for one person (people may share a browser). */
export type HeroOverrides = Readonly<Record<string, HeroVariant>>;

type Stored = Record<string, Record<string, HeroVariant>>;

function readAll(): Stored {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) return {};
    const parsed: unknown = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)
      ? (parsed as Stored)
      : {};
  } catch {
    return {};
  }
}

const isVariant = (value: unknown): value is HeroVariant => value === 'full' || value === 'slim';

/** This person's per-page overrides; empty when there are none or storage isn't available. */
export function readHeroOverrides(personId: string): HeroOverrides {
  const mine = readAll()[personId];
  if (typeof mine !== 'object') return {};
  return Object.fromEntries(Object.entries(mine).filter(([, v]) => isVariant(v)));
}

/**
 * Records the header form for one page and returns this person's overrides. Choosing the
 * person's default again removes the override, so a later change in My settings applies there.
 */
export function writeHeroOverride(
  personId: string,
  page: string,
  variant: HeroVariant,
  defaultVariant: HeroVariant,
): HeroOverrides {
  const all = readAll();
  const others = Object.entries(readHeroOverrides(personId)).filter(([key]) => key !== page);
  const mine: HeroOverrides = Object.fromEntries(
    variant === defaultVariant ? others : [...others, [page, variant]],
  );
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...all, [personId]: mine }));
  } catch {
    // Storage is full or blocked: the choice still holds on this page until it reloads.
  }
  return mine;
}

/**
 * The page an override belongs to: the route's pattern, so every award record shares one choice
 * (`/awards/:id`) rather than one per award.
 */
export function pagePattern(
  pathname: string,
  params: Readonly<Record<string, string | undefined>>,
) {
  let pattern = pathname.replace(/\/+$/, '') || '/';
  for (const [name, value] of Object.entries(params)) {
    if (value === undefined || value === '' || name === '*') continue;
    pattern = pattern
      .split('/')
      .map((part) => (part === encodeURIComponent(value) || part === value ? `:${name}` : part))
      .join('/');
  }
  return pattern;
}
